import {fileToBase64Object} from '@coolcolduk/util';
import {addDoc, collection, serverTimestamp} from 'firebase/firestore';
import {useCallback, useState} from 'react';
import {z} from 'zod';
import {ContactCategory} from '../__generated__/resolvers-types';
import {env} from '../constants';
import {firebaseFirestore} from '../setup/firebase';

const Base64ObjectSchema = z.object({
  data: z.string(),
  name: z.string(),
  type: z.string(),
});

const ContactMessageSchema = z.object({
  name: z.string().min(1),
  email: z.string().email(),
  category: z.nativeEnum(ContactCategory),
  message: z.string().min(1),
  attachments: z.array(Base64ObjectSchema),
  status: z.literal('active'),
});

export type ContactMessagePayload = z.infer<typeof ContactMessageSchema>;

/**
 * Hook for creating contact messages with file attachments
 * Writes directly to Firestore
 */
export default function useCreateContactMessage() {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);
  const [success, setSuccess] = useState(false);

  const writeContactMessageToFirestore = useCallback(
    async (name: string, email: string, category: ContactCategory, message: string, attachments: File[]) => {
      setLoading(true);
      setError(null);
      setSuccess(false);

      try {
        const attachmentData = await Promise.all(attachments.map((a) => fileToBase64Object(a)));

        const payload: ContactMessagePayload = {
          name,
          email,
          category,
          message,
          attachments: attachmentData,
          status: 'active',
        };

        const validatedPayload = ContactMessageSchema.parse(payload);

        const docRef = await addDoc(collection(firebaseFirestore, env.collection.contactMessages), {
          ...validatedPayload,
          createdTimestamp: serverTimestamp(),
        });

        setSuccess(true);
        return docRef;
      } catch (err) {
        const errorInstance = err instanceof Error ? err : new Error('Failed to create contact message');
        setError(errorInstance);
        throw errorInstance;
      } finally {
        setLoading(false);
      }
    },
    [],
  );

  return {
    writeContactMessageToFirestore,
    loading,
    error,
    success,
  };
}
